import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Wallet, Plus, RefreshCw, Trash2, TrendingUp, TrendingDown,
         AlertTriangle, FileDown, Send, Upload, Paperclip, FileText, X,
         Camera, FolderUp } from "lucide-react";

/**
 * FinancesAdminPanel — Suivi financier général SAFUAYE
 * Entrées de fonds (Président International) vs Dépenses catégorisées.
 * + Alerte solde négatif · Rapport trimestriel PDF · Justificatifs · Import PDF Gemini.
 */
const CATS = [
  { id: "gala", label: "🎉 Gala", color: "bg-orange-100 text-orange-800" },
  { id: "academie", label: "⚽ Académie", color: "bg-emerald-100 text-emerald-800" },
  { id: "odon_itsou", label: "🥁 Odôn-Itsou", color: "bg-purple-100 text-purple-800" },
  { id: "sante_avc", label: "🏥 Santé AVC", color: "bg-red-100 text-red-800" },
  { id: "admin", label: "📋 Administration", color: "bg-slate-100 text-slate-800" },
  { id: "siege", label: "🏠 Siège", color: "bg-amber-100 text-amber-800" },
  { id: "scolarite", label: "🎒 Scolarité", color: "bg-indigo-100 text-indigo-800" },
  { id: "autre", label: "◾ Autre", color: "bg-gray-100 text-gray-700" },
];
const fmt = (n) => (n ?? 0).toLocaleString("fr-FR") + " FCFA";
const currentQuarter = () => {
  const d = new Date();
  return { year: d.getFullYear(), quarter: Math.floor(d.getMonth() / 3) + 1 };
};

export default function FinancesAdminPanel({ API }) {
  const pin = "SAFUAYE2024";
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ kind: "", category: "" });
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    kind: "out", category: "gala", amount_fcfa: "",
    label: "", date: new Date().toISOString().slice(0, 10),
    payer_or_payee: "", notes: "",
    entity: "togo", currency: "XOF", original_amount: "",
  });
  const [rates, setRates] = useState({ XOF: 1, EUR: 655.957, GBP: 755.0 });
  const [busy, setBusy] = useState("");
  const [importPreview, setImportPreview] = useState(null); // { rows, source_filename }
  const [reportQ, setReportQ] = useState(currentQuarter());
  const [bulkModal, setBulkModal] = useState(null); // { year, missing: [], files: [], results: [] }
  const fileImportRef = useRef(null);
  const receiptRefs = useRef({}); // per-entry refs
  const cameraRefs = useRef({});  // per-entry camera capture refs

  const load = async () => {
    try {
      const q = { pin, ...(filter.kind && { kind: filter.kind }),
                       ...(filter.category && { category: filter.category }) };
      const [s, l, x] = await Promise.all([
        axios.get(`${API}/finances/summary`, { params: { pin } }),
        axios.get(`${API}/finances/entries`, { params: q }),
        axios.get(`${API}/finances/exchange-rates`, { params: { pin } }).catch(() => null),
      ]);
      setSummary(s.data);
      setItems(l.data.items || []);
      if (x?.data) setRates(x.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const save = async () => {
    if (!form.label.trim() || !form.amount_fcfa) {
      alert("Libellé et montant requis"); return;
    }
    // If currency is EUR or GBP, convert original_amount to XOF
    const cur = form.currency || "XOF";
    let amount_fcfa = Number(form.amount_fcfa);
    let original_amount = null;
    if (cur !== "XOF") {
      const orig = Number(form.original_amount || form.amount_fcfa);
      amount_fcfa = Math.round(orig * (rates[cur] || 1));
      original_amount = orig;
    }
    try {
      await axios.post(`${API}/finances/entries`, {
        pin, ...form, amount_fcfa, currency: cur,
        original_amount,
      });
      setForm({ ...form, amount_fcfa: "", label: "", notes: "", original_amount: "" });
      setShow(false); load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    await axios.delete(`${API}/finances/entries/${id}`, { params: { pin } });
    load();
  };

  const catLabel = (id) => CATS.find(c => c.id === id)?.label || id;
  const catColor = (id) => CATS.find(c => c.id === id)?.color || "bg-gray-100";

  // Alerte manuelle
  const checkAlert = async (force = true) => {
    setBusy("alert");
    try {
      const r = await axios.post(`${API}/finances/check-negative-balance`,
                                    null, { params: { pin, force } });
      if (r.data.triggered) {
        alert(`✅ Alerte envoyée au Président International (${r.data.recipient}).\nSolde : ${fmt(r.data.balance)}`);
      } else {
        alert(`ℹ️ ${r.data.reason || "Aucune alerte requise."}`);
      }
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  // Rapport trimestriel — télécharger
  const downloadQuarterly = async () => {
    setBusy("download");
    try {
      const r = await axios.get(`${API}/finances/quarterly-report`, {
        params: { pin, year: reportQ.year, quarter: reportQ.quarter },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SAFUAYE_Etat_Depenses_T${reportQ.quarter}_${reportQ.year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  // Rapport trimestriel — envoyer par email
  const sendQuarterly = async () => {
    if (!window.confirm(`Envoyer le rapport T${reportQ.quarter} ${reportQ.year} au Président International + associationsafuaye@gmail.com ?`)) return;
    setBusy("send");
    try {
      const r = await axios.post(`${API}/finances/quarterly-report/send`, null, {
        params: { pin, year: reportQ.year, quarter: reportQ.quarter },
      });
      const sent = (r.data.recipients || []).filter(x => x.sent).length;
      alert(`✅ Rapport envoyé à ${sent} destinataire(s).`);
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  // Justificatif — upload pour une ligne
  const uploadReceipt = async (entryId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("pin", pin);
    fd.append("file", file);
    try {
      await axios.post(`${API}/finances/entries/${entryId}/receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
  };
  const deleteReceipt = async (entryId) => {
    if (!window.confirm("Supprimer le justificatif ?")) return;
    try {
      await axios.delete(`${API}/finances/entries/${entryId}/receipt`, { params: { pin } });
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
  };

  // Certificat de don officiel (pour les entrées "in")
  const generateCertificate = async (entryId, donor) => {
    if (!window.confirm(`Générer un certificat de don officiel pour ${donor} ?`)) return;
    try {
      const r = await axios.post(`${API}/finances/entries/${entryId}/donation-certificate`, null, { params: { pin } });
      alert(`✅ Certificat généré : ${r.data.reference}\n\nOuverture du PDF…`);
      window.open(r.data.url, "_blank", "noopener");
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
  };

  // Import PDF via Gemini
  const importPdf = async (file) => {
    if (!file) return;
    setBusy("import");
    try {
      const fd = new FormData();
      fd.append("pin", pin);
      fd.append("file", file);
      fd.append("default_kind", "out");
      fd.append("default_category", "autre");
      const r = await axios.post(`${API}/finances/import-pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview(r.data);
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };
  const commitImport = async () => {
    if (!importPreview?.rows?.length) return;
    setBusy("commit");
    try {
      const r = await axios.post(`${API}/finances/import-commit`, {
        pin, rows: importPreview.rows,
      });
      alert(`✅ ${r.data.inserted} ligne(s) ajoutée(s).`);
      setImportPreview(null);
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  // Bulk justificatifs — modal
  const openBulkModal = async () => {
    setBusy("bulk-open");
    try {
      const r = await axios.get(`${API}/finances/missing-receipts`,
                                    { params: { pin, year: reportQ.year } });
      setBulkModal({ year: reportQ.year, missing: r.data.items || [],
                       files: [], results: [], assignments: {} });
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const bulkSubmit = async () => {
    if (!bulkModal?.files?.length) { alert("Sélectionne au moins un fichier"); return; }
    setBusy("bulk-submit");
    try {
      const fd = new FormData();
      fd.append("pin", pin);
      fd.append("year", bulkModal.year);
      fd.append("assignments", JSON.stringify(bulkModal.assignments || {}));
      bulkModal.files.forEach(f => fd.append("files", f));
      const r = await axios.post(`${API}/finances/bulk-upload-receipts`, fd,
                                    { headers: { "Content-Type": "multipart/form-data" } });
      // Refresh missing list to avoid overwriting a receipt on a 2nd submit
      const refreshed = await axios.get(`${API}/finances/missing-receipts`,
                                           { params: { pin, year: bulkModal.year } });
      setBulkModal({ ...bulkModal,
                      missing: refreshed.data.items || [],
                      files: [], assignments: {},
                      results: r.data.results || [] });
      alert(`✅ ${r.data.attached}/${r.data.total} justificatifs attachés.`);
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const isNegative = summary && summary.balance < 0;

  return (
    <div className="bg-white rounded-xl p-5 shadow-lg mt-6" data-testid="finances-admin-panel">
      <h4 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
        <Wallet className="w-6 h-6 text-emerald-700" />
        Finances SAFUAYE — Tableau de bord général
      </h4>

      {/* Bandeau d'alerte si solde négatif */}
      {isNegative && (
        <div className="bg-red-50 border-l-4 border-red-600 p-3 mb-4 flex items-start gap-3" data-testid="fin-alert-banner">
          <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-900">Solde net négatif : {fmt(summary.balance)}</p>
            <p className="text-xs text-red-700">Une alerte automatique est envoyée quotidiennement au Président International (association@safuaye.org).</p>
          </div>
          <button onClick={() => checkAlert(true)} disabled={busy === "alert"}
                  data-testid="fin-force-alert-btn"
                  className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded font-semibold whitespace-nowrap disabled:opacity-50">
            {busy === "alert" ? "..." : "🚨 Envoyer alerte maintenant"}
          </button>
        </div>
      )}

      {/* Cartes récap */}
      {summary && (
        <div className="grid md:grid-cols-3 gap-3 mb-5">
          <Card icon={<TrendingUp className="w-5 h-5" />} title="Entrées de fonds"
                value={fmt(summary.total_in)} color="bg-emerald-600" testId="fin-total-in" />
          <Card icon={<TrendingDown className="w-5 h-5" />} title="Dépenses totales"
                value={fmt(summary.total_out)} color="bg-red-600" testId="fin-total-out" />
          <Card icon={<Wallet className="w-5 h-5" />} title="Solde net"
                value={fmt(summary.balance)}
                color={summary.balance >= 0 ? "bg-navy-800" : "bg-orange-600"}
                testId="fin-balance" />
        </div>
      )}

      {/* Rapport trimestriel + Import PDF */}
      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="fin-quarterly-block">
          <p className="text-xs font-bold uppercase text-blue-800 mb-2 flex items-center gap-1">
            <FileText className="w-3.5 h-3.5" /> Rapport trimestriel officiel
          </p>
          <div className="flex flex-wrap gap-2 items-center text-xs">
            <select value={reportQ.year} onChange={e => setReportQ({ ...reportQ, year: Number(e.target.value) })}
                    data-testid="fin-report-year"
                    className="px-2 py-1.5 border rounded">
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={reportQ.quarter} onChange={e => setReportQ({ ...reportQ, quarter: Number(e.target.value) })}
                    data-testid="fin-report-quarter"
                    className="px-2 py-1.5 border rounded">
              {[1, 2, 3, 4].map(q => <option key={q} value={q}>T{q}</option>)}
            </select>
            <button onClick={downloadQuarterly} disabled={busy === "download"}
                    data-testid="fin-report-download"
                    className="inline-flex items-center gap-1 bg-blue-700 hover:bg-blue-800 text-white px-3 py-1.5 rounded font-semibold disabled:opacity-50">
              <FileDown className="w-3.5 h-3.5" /> {busy === "download" ? "..." : "Télécharger"}
            </button>
            <button onClick={sendQuarterly} disabled={busy === "send"}
                    data-testid="fin-report-send"
                    className="inline-flex items-center gap-1 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1.5 rounded font-semibold disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> {busy === "send" ? "..." : "Envoyer aux Sages"}
            </button>
            <button onClick={openBulkModal} disabled={busy === "bulk-open"}
                    data-testid="fin-bulk-audit-btn"
                    title="Attacher en lot les justificatifs manquants — pour audit Charity Commission"
                    className="inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded font-semibold disabled:opacity-50">
              <FolderUp className="w-3.5 h-3.5" /> {busy === "bulk-open" ? "..." : "📦 Audit — Justificatifs en lot"}
            </button>
          </div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3" data-testid="fin-import-block">
          <p className="text-xs font-bold uppercase text-purple-800 mb-2 flex items-center gap-1">
            ✨ Importer un PDF (Gemini IA)
          </p>
          <div className="flex flex-wrap gap-2 items-center text-xs">
            <input type="file" accept=".pdf" ref={fileImportRef}
                   onChange={e => importPdf(e.target.files?.[0])}
                   data-testid="fin-import-file"
                   className="text-xs" />
            <button onClick={() => fileImportRef.current?.click()} disabled={busy === "import"}
                    data-testid="fin-import-btn"
                    className="inline-flex items-center gap-1 bg-purple-700 hover:bg-purple-800 text-white px-3 py-1.5 rounded font-semibold disabled:opacity-50">
              <Upload className="w-3.5 h-3.5" /> {busy === "import" ? "Analyse..." : "Choisir un PDF"}
            </button>
          </div>
          <p className="text-[10px] text-purple-700 mt-1">Le PDF est analysé par Gemini pour extraire automatiquement les lignes.</p>
        </div>
      </div>

      {/* Prévisualisation import Gemini */}
      {importPreview && (
        <div className="bg-white border-2 border-purple-400 rounded-lg p-3 mb-4" data-testid="fin-import-preview">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-purple-900">
              ✨ {importPreview.detected} ligne(s) détectée(s) dans <em>{importPreview.source_filename}</em>
            </p>
            <button onClick={() => setImportPreview(null)}
                    className="p-1 hover:bg-purple-100 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
          {importPreview.rows.length === 0 ? (
            <p className="text-xs text-gray-600">Aucune ligne financière détectée. Essayez un autre PDF.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-purple-100">
                  <tr>
                    <th className="p-1 text-left">Date</th><th className="p-1 text-left">Type</th>
                    <th className="p-1 text-left">Catégorie</th><th className="p-1 text-left">Libellé</th>
                    <th className="p-1 text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.rows.map((r, i) => (
                    <tr key={i} className="border-b" data-testid={`fin-import-row-${i}`}>
                      <td className="p-1 font-mono">
                        <input value={r.date} onChange={e => {
                          const rows = [...importPreview.rows]; rows[i] = { ...r, date: e.target.value };
                          setImportPreview({ ...importPreview, rows });
                        }} className="w-24 border rounded px-1" />
                      </td>
                      <td className="p-1">
                        <select value={r.kind} onChange={e => {
                          const rows = [...importPreview.rows]; rows[i] = { ...r, kind: e.target.value };
                          setImportPreview({ ...importPreview, rows });
                        }} className="border rounded px-1">
                          <option value="out">Dépense</option><option value="in">Entrée</option>
                        </select>
                      </td>
                      <td className="p-1">
                        <select value={r.category} onChange={e => {
                          const rows = [...importPreview.rows]; rows[i] = { ...r, category: e.target.value };
                          setImportPreview({ ...importPreview, rows });
                        }} className="border rounded px-1">
                          {CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                        </select>
                      </td>
                      <td className="p-1">
                        <input value={r.label} onChange={e => {
                          const rows = [...importPreview.rows]; rows[i] = { ...r, label: e.target.value };
                          setImportPreview({ ...importPreview, rows });
                        }} className="w-full border rounded px-1" />
                      </td>
                      <td className="p-1 text-right">
                        <input type="number" value={r.amount_fcfa} onChange={e => {
                          const rows = [...importPreview.rows]; rows[i] = { ...r, amount_fcfa: Number(e.target.value) };
                          setImportPreview({ ...importPreview, rows });
                        }} className="w-24 border rounded px-1 text-right" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex gap-2 mt-2">
                <button onClick={commitImport} disabled={busy === "commit"}
                        data-testid="fin-import-commit"
                        className="bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-50">
                  {busy === "commit" ? "..." : `💾 Valider et ajouter les ${importPreview.rows.length} lignes`}
                </button>
                <button onClick={() => setImportPreview(null)}
                        className="bg-slate-200 hover:bg-slate-300 px-3 py-1.5 rounded text-xs">
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Breakdown par catégorie */}
      {summary && summary.by_category.length > 0 && (
        <div className="bg-slate-50 rounded-lg p-3 mb-4">
          <p className="text-xs font-bold uppercase text-slate-700 mb-2">Par catégorie</p>
          <div className="space-y-1">
            {summary.by_category.map(c => (
              <div key={c.category} className="flex items-center gap-2 text-sm"
                   data-testid={`fin-cat-${c.category}`}>
                <span className="w-40 truncate">{catLabel(c.category)}</span>
                <div className="flex-1 flex gap-1 h-4">
                  {c.in > 0 && (
                    <div className="bg-emerald-500 rounded" title={`Entrées: ${fmt(c.in)}`}
                         style={{ width: `${(c.in / summary.total_in * 100) || 0}%` }} />
                  )}
                  {c.out > 0 && (
                    <div className="bg-red-500 rounded" title={`Dépenses: ${fmt(c.out)}`}
                         style={{ width: `${(c.out / summary.total_out * 100) || 0}%` }} />
                  )}
                </div>
                <span className={`w-32 text-right text-xs font-mono ${c.balance < 0 ? "text-red-700" : "text-emerald-700"}`}>
                  {fmt(c.balance)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Barre d'actions */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <button onClick={() => setShow(!show)}
                data-testid="fin-add-toggle"
                className="inline-flex items-center gap-1 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 rounded text-sm font-semibold">
          <Plus className="w-4 h-4" /> {show ? "Fermer" : "Nouvelle entrée"}
        </button>
        <button onClick={load}
                className="inline-flex items-center gap-1 bg-slate-200 hover:bg-slate-300 px-3 py-2 rounded text-sm">
          <RefreshCw className="w-4 h-4" /> Rafraîchir
        </button>
        <select value={filter.kind} onChange={e => setFilter({ ...filter, kind: e.target.value })}
                data-testid="fin-filter-kind"
                className="border rounded px-2 py-1 text-xs">
          <option value="">Tous types</option>
          <option value="in">Entrées</option>
          <option value="out">Dépenses</option>
        </select>
        <select value={filter.category} onChange={e => setFilter({ ...filter, category: e.target.value })}
                data-testid="fin-filter-cat"
                className="border rounded px-2 py-1 text-xs">
          <option value="">Toutes catégories</option>
          {CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>

      {/* Formulaire d'ajout */}
      {show && (
        <div className="bg-emerald-50 border border-emerald-300 p-3 rounded mb-3" data-testid="fin-add-form">
          <div className="grid md:grid-cols-3 gap-2">
            <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}
                    data-testid="fin-kind" className="px-3 py-2 border rounded text-sm">
              <option value="out">💸 Dépense</option>
              <option value="in">💰 Entrée de fonds</option>
            </select>
            <select value={form.entity} onChange={e => setForm({ ...form, entity: e.target.value })}
                    data-testid="fin-entity" className="px-3 py-2 border rounded text-sm">
              <option value="togo">🇹🇬 SAFUAYE Togo</option>
              <option value="uk">🇬🇧 SAFUAYE UK</option>
            </select>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    data-testid="fin-category" className="px-3 py-2 border rounded text-sm">
              {CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <div className="flex gap-1">
              <select value={form.currency}
                      onChange={e => {
                        const cur = e.target.value;
                        if (cur === "XOF") {
                          // Basculer vers XOF : le champ montant devient direct-FCFA, on convertit l'original_amount en FCFA si dispo
                          const fcfa = form.original_amount
                            ? String(Math.round(Number(form.original_amount) * (rates[form.currency] || 1)))
                            : form.amount_fcfa;
                          setForm({ ...form, currency: cur, amount_fcfa: fcfa, original_amount: "" });
                        } else {
                          // Basculer entre devises étrangères OU depuis XOF : recalculer amount_fcfa
                          const rate = rates[cur] || 1;
                          const orig = form.original_amount || (form.currency === "XOF" && form.amount_fcfa
                            ? String((Number(form.amount_fcfa) / (rates[cur] || 1)).toFixed(2))
                            : "");
                          const amt = orig ? String(Math.round(Number(orig) * rate)) : "";
                          setForm({ ...form, currency: cur, original_amount: orig, amount_fcfa: amt });
                        }
                      }}
                      data-testid="fin-currency" className="px-2 py-2 border rounded text-sm w-24">
                <option value="XOF">FCFA</option>
                <option value="EUR">EUR €</option>
                <option value="GBP">GBP £</option>
              </select>
              <input type="number" placeholder={form.currency === "XOF" ? "Montant FCFA *" : `Montant ${form.currency} *`}
                     value={form.currency === "XOF" ? form.amount_fcfa : form.original_amount}
                     onChange={e => {
                       if (form.currency === "XOF") {
                         setForm({ ...form, amount_fcfa: e.target.value });
                       } else {
                         const orig = e.target.value;
                         const rate = rates[form.currency] || 1;
                         setForm({ ...form, original_amount: orig,
                                    amount_fcfa: orig ? String(Math.round(Number(orig) * rate)) : "" });
                       }
                     }}
                     data-testid="fin-amount" className="flex-1 px-3 py-2 border rounded text-sm" />
            </div>
            {form.currency !== "XOF" && form.amount_fcfa && (
              <div className="col-span-full text-xs text-slate-600 bg-slate-100 rounded px-2 py-1" data-testid="fin-conv-hint">
                → ≈ <strong>{Number(form.amount_fcfa).toLocaleString("fr-FR")} FCFA</strong>
                {" "}(taux {form.currency} : {rates[form.currency]})
              </div>
            )}
            <input placeholder="Libellé *" value={form.label}
                   onChange={e => setForm({ ...form, label: e.target.value })}
                   data-testid="fin-label" className="md:col-span-2 px-3 py-2 border rounded text-sm" />
            <input type="date" value={form.date}
                   onChange={e => setForm({ ...form, date: e.target.value })}
                   className="px-3 py-2 border rounded text-sm" />
            <input placeholder="Payeur / bénéficiaire (optionnel)" value={form.payer_or_payee}
                   onChange={e => setForm({ ...form, payer_or_payee: e.target.value })}
                   className="md:col-span-2 px-3 py-2 border rounded text-sm" />
            <input placeholder="Notes (facultatif)" value={form.notes}
                   onChange={e => setForm({ ...form, notes: e.target.value })}
                   className="px-3 py-2 border rounded text-sm" />
          </div>
          <button onClick={save} data-testid="fin-save"
                  className="mt-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded text-sm font-semibold">
            💾 Enregistrer
          </button>
        </div>
      )}

      {/* Liste des entrées */}
      {items.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">Aucune entrée.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Type</th>
                <th className="p-2 text-left">Catégorie</th>
                <th className="p-2 text-left">Libellé</th>
                <th className="p-2 text-right">Montant</th>
                <th className="p-2 text-left">Justif.</th>
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(e => (
                <tr key={e.id} className="border-b hover:bg-slate-50" data-testid={`fin-row-${e.id}`}>
                  <td className="p-2 font-mono text-[11px]">{e.date}</td>
                  <td className="p-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${e.kind === "in" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                      {e.kind === "in" ? "💰 Entrée" : "💸 Dépense"}
                    </span>
                  </td>
                  <td className="p-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] ${catColor(e.category)}`}>
                      {catLabel(e.category)}
                    </span>
                  </td>
                  <td className="p-2">
                    <p className="font-semibold">{e.label}</p>
                    {e.notes && <p className="text-[10px] text-gray-500 italic">{e.notes}</p>}
                  </td>
                  <td className={`p-2 text-right font-mono font-bold ${e.kind === "in" ? "text-emerald-700" : "text-red-700"}`}>
                    <div>{e.kind === "in" ? "+" : "−"} {fmt(e.amount_fcfa)}</div>
                    {e.entity === "uk" && (
                      <div className="text-[9px] font-normal text-blue-700">🇬🇧 UK</div>
                    )}
                    {e.currency && e.currency !== "XOF" && e.original_amount && (
                      <div className="text-[9px] font-normal text-slate-500 italic">
                        {e.currency === "EUR" ? "€" : e.currency === "GBP" ? "£" : ""}
                        {Number(e.original_amount).toLocaleString("fr-FR")} {e.currency}
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    {e.receipt_url ? (
                      <div className="flex items-center gap-1">
                        <a href={`${API.replace(/\/api$/, "")}${e.receipt_url}`} target="_blank" rel="noreferrer"
                           data-testid={`fin-receipt-view-${e.id}`}
                           className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-[10px] font-semibold">
                          <Paperclip className="w-3 h-3" /> Voir
                        </a>
                        <button onClick={() => deleteReceipt(e.id)}
                                data-testid={`fin-receipt-delete-${e.id}`}
                                className="text-red-600 hover:bg-red-50 p-0.5 rounded">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                               ref={el => { if (el) receiptRefs.current[e.id] = el; }}
                               onChange={ev => uploadReceipt(e.id, ev.target.files?.[0])}
                               className="hidden" />
                        <button onClick={() => receiptRefs.current[e.id]?.click()}
                                data-testid={`fin-receipt-upload-${e.id}`}
                                title="Choisir un fichier PDF ou image"
                                className="inline-flex items-center gap-1 text-purple-700 hover:bg-purple-50 px-1.5 py-0.5 rounded text-[10px] font-semibold">
                          <Upload className="w-3 h-3" /> Ajouter
                        </button>
                        <input type="file" accept="image/*" capture="environment"
                               ref={el => { if (el) cameraRefs.current[e.id] = el; }}
                               onChange={ev => uploadReceipt(e.id, ev.target.files?.[0])}
                               className="hidden" />
                        <button onClick={() => cameraRefs.current[e.id]?.click()}
                                data-testid={`fin-receipt-camera-${e.id}`}
                                title="Prendre en photo (mobile)"
                                className="inline-flex items-center gap-1 text-emerald-700 hover:bg-emerald-50 px-1.5 py-0.5 rounded text-[10px] font-semibold md:hidden">
                          <Camera className="w-3 h-3" /> 📸
                        </button>
                        <button onClick={() => cameraRefs.current[e.id]?.click()}
                                data-testid={`fin-receipt-camera-desktop-${e.id}`}
                                title="Prendre en photo"
                                className="hidden md:inline-flex items-center gap-1 text-emerald-700 hover:bg-emerald-50 px-1.5 py-0.5 rounded text-[10px] font-semibold">
                          <Camera className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    {e.kind === "in" && (
                      <button onClick={() => generateCertificate(e.id, e.payer_or_payee || "Donateur")}
                              data-testid={`fin-cert-${e.id}`}
                              title={e.donation_certificate ? `Réf : ${e.donation_certificate.reference}` : "Générer un certificat de don officiel"}
                              className={`inline-flex items-center gap-0.5 mr-1 p-1 rounded text-[10px] font-semibold ${e.donation_certificate ? "text-emerald-700 bg-emerald-50" : "text-blue-700 hover:bg-blue-50"}`}>
                        📜 {e.donation_certificate ? "Certif." : "Certif."}
                      </button>
                    )}
                    <button onClick={() => del(e.id)}
                            data-testid={`fin-delete-${e.id}`}
                            className="text-red-600 hover:bg-red-50 p-1 rounded">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Bulk Justificatifs Modal — audit Charity Commission */}
      {bulkModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3" data-testid="fin-bulk-modal">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            <div className="bg-amber-600 text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">📦 Audit Charity Commission — Justificatifs en lot</h3>
                <p className="text-xs text-amber-100">Année {bulkModal.year} · {bulkModal.missing.length} dépenses sans justificatif</p>
              </div>
              <button onClick={() => setBulkModal(null)} className="hover:bg-amber-700 p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3 text-xs text-amber-900">
                <p><strong>Comment ça marche ?</strong></p>
                <ol className="list-decimal ml-4 space-y-1 mt-1">
                  <li>Sélectionne plusieurs fichiers (PDF ou images)</li>
                  <li>Le nom du fichier est utilisé pour auto-associer à la dépense correspondante (ex: <em>Quittance_Ministere.pdf</em> → « Quittance Ministère MATDDT »)</li>
                  <li>Tu peux ajuster manuellement les associations dans le tableau ci-dessous</li>
                  <li>Clique sur <strong>Envoyer</strong> pour attacher en lot</li>
                </ol>
              </div>
              <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp"
                     onChange={ev => setBulkModal({ ...bulkModal, files: Array.from(ev.target.files || []) })}
                     data-testid="fin-bulk-files"
                     className="mb-3 text-sm w-full border rounded p-2" />
              {bulkModal.files.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-bold text-slate-700 mb-1">{bulkModal.files.length} fichier(s) sélectionné(s) — assigner à une dépense :</p>
                  <table className="w-full text-xs border">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="p-2 text-left">Fichier</th>
                        <th className="p-2 text-left">Assigner à la dépense</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkModal.files.map((f, i) => (
                        <tr key={i} className="border-b" data-testid={`fin-bulk-file-${i}`}>
                          <td className="p-2 font-mono text-[10px]">{f.name}</td>
                          <td className="p-2">
                            <select value={bulkModal.assignments[f.name] || ""}
                                    onChange={ev => setBulkModal({
                                      ...bulkModal,
                                      assignments: { ...bulkModal.assignments, [f.name]: ev.target.value }
                                    })}
                                    data-testid={`fin-bulk-assign-${i}`}
                                    className="border rounded px-2 py-1 text-xs w-full">
                              <option value="">(auto-détection par nom de fichier)</option>
                              {bulkModal.missing.map(m => (
                                <option key={m.id} value={m.id}>
                                  {m.date} · {catLabel(m.category)} · {m.label?.slice(0, 45)} · {fmt(m.amount_fcfa)}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {bulkModal.results.length > 0 && (
                <div className="bg-slate-50 border rounded p-2 text-xs mt-2">
                  <p className="font-bold text-slate-700 mb-1">Résultats :</p>
                  {bulkModal.results.map((r, i) => {
                    const s = r.match_score ?? 0;
                    const conf = s >= 0.8 ? { icon: "🎯", label: "Haute", color: "bg-emerald-100 text-emerald-800" }
                              : s >= 0.5 ? { icon: "🔸", label: "Moyenne", color: "bg-amber-100 text-amber-800" }
                              : s > 0    ? { icon: "⚠️", label: "Faible", color: "bg-red-100 text-red-800" }
                              :            { icon: "❌", label: "Aucune", color: "bg-slate-200 text-slate-700" };
                    return (
                      <div key={i} className={`flex items-center gap-2 py-1 border-b border-slate-200 ${r.ok ? "text-emerald-900" : "text-red-800"}`}
                           data-testid={`fin-bulk-result-${i}`}>
                        <span className="text-base">{r.ok ? "✅" : "❌"}</span>
                        <span className="flex-1 truncate"><strong>{r.filename}</strong>{r.matched_label ? ` → ${r.matched_label.slice(0, 50)}` : ""}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${conf.color}`}
                              title={r.match_reason || ""}>
                          {conf.icon} {conf.label} ({Math.round(s * 100)}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {bulkModal.missing.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs font-bold text-slate-700">
                    📋 Voir les {bulkModal.missing.length} dépenses sans justificatif
                  </summary>
                  <div className="overflow-x-auto mt-1">
                    <table className="w-full text-[11px]">
                      <thead className="bg-slate-100">
                        <tr><th className="p-1 text-left">Date</th><th className="p-1 text-left">Catégorie</th>
                            <th className="p-1 text-left">Libellé</th><th className="p-1 text-right">Montant</th></tr>
                      </thead>
                      <tbody>
                        {bulkModal.missing.map(m => (
                          <tr key={m.id} className="border-b">
                            <td className="p-1 font-mono">{m.date}</td>
                            <td className="p-1">{catLabel(m.category)}</td>
                            <td className="p-1">{m.label}</td>
                            <td className="p-1 text-right font-mono">{fmt(m.amount_fcfa)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </div>
            <div className="p-4 border-t flex gap-2 justify-end">
              <button onClick={() => setBulkModal(null)}
                      className="bg-slate-200 hover:bg-slate-300 px-4 py-2 rounded text-sm">
                Fermer
              </button>
              <button onClick={bulkSubmit} disabled={busy === "bulk-submit" || !bulkModal.files.length}
                      data-testid="fin-bulk-submit"
                      className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded text-sm font-bold disabled:opacity-50">
                {busy === "bulk-submit" ? "..." : `📤 Envoyer ${bulkModal.files.length} fichier(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ icon, title, value, color, testId }) {
  return (
    <div className={`${color} text-white p-4 rounded-lg shadow`} data-testid={testId}>
      <div className="flex items-center gap-2 opacity-90 text-xs uppercase font-semibold">
        {icon} {title}
      </div>
      <p className="mt-1 text-2xl font-bold font-mono">{value}</p>
    </div>
  );
}
